import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect
} from 'react'
import { useSelector } from 'react-redux'
import { Icon, Modal, Button, Popconfirm } from 'antd'
import useShotGenerationStyles from '../../../../hooks/useShotGenerationStyles'
import StyleCard from '../../../StyleCard/StyleCard'
import CreateStyleModal from './CreateStyleModal'
import './ShotGenerationStyle.scss'

const ShotGenerationStyle = forwardRef(
  (
    { formData, setFormData, onStyleSelect, isDraftMode = false, videoId },
    ref
  ) => {
    const {
      allStyles,
      systemStyles,
      customStyles,
      isCreatingStyle,
      uploadProgress,
      creationProgress,
      isModalVisible,
      isLoadingStyles,
      createCustomStyle,
      deleteStyle,
      deletingStyleId,
      openCreateStyleModal,
      closeCreateStyleModal,
      currentAction
    } = useShotGenerationStyles({ videoId })

    useImperativeHandle(ref, () => ({
      openCreateStyleModal
    }))

    // Auto-select default style when styles load and no style is selected

    const userInfo = useSelector(state => state.user.info)
    const isSuperAdmin = userInfo?.isSuperAdmin

    // Video Preview Modal State (For Style Cards)
    const [previewModalVisible, setPreviewModalVisible] = useState(false)
    const [previewStyleInfo, setPreviewStyleInfo] = useState({
      title: '',
      isCustom: false,
      thumbnailVideo: null,
      referenceVideos: []
    })
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0)
    const [activeVideoTab, setActiveVideoTab] = useState('thumbnail') // 'thumbnail' or 'reference'

    const getStyleId = style => style?.id || style?.raw?.id || style?.key

    const handleStyleSelect = style => {
      const normalizedStyle = style
        ? {
            ...style,
            id: getStyleId(style)
          }
        : style

      if (onStyleSelect) {
        onStyleSelect(normalizedStyle)
      } else {
        setFormData(prev => ({
          ...prev,
          shotGenerationStyle: normalizedStyle
        }))
      }
    }

    const openVideoPreview = styleInfo => {
      // styleInfo: { title, isCustom, thumbnailVideo, referenceVideos }
      setPreviewStyleInfo({
        title: styleInfo.title || '',
        isCustom: styleInfo.isCustom || false,
        thumbnailVideo: styleInfo.thumbnailVideo || null,
        referenceVideos: styleInfo.referenceVideos || []
      })
      setCurrentPreviewIndex(0)
      setActiveVideoTab('thumbnail')
      setPreviewModalVisible(true)
    }

    const handleDeleteStyle = style => {
      Modal.confirm({
        title: 'Delete Style',
        content: `Are you sure you want to delete "${style.title}"? This action cannot be undone.`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        centered: true,
        onOk: () => {
          deleteStyle(getStyleId(style), style.isSystem)
        }
      })
    }

    // Get current video to display based on active tab
    const getCurrentVideo = () => {
      if (activeVideoTab === 'thumbnail') {
        return previewStyleInfo.thumbnailVideo
      } else {
        const refVideo = previewStyleInfo.referenceVideos[currentPreviewIndex]
        return typeof refVideo === 'string' ? refVideo : refVideo?.video_link
      }
    }

    const renderStyleGrid = styles => (
      <div className="shot-generation-style-cards">
        {styles.map(style => (
          <StyleCard
            key={getStyleId(style)}
            style={style}
            isSelected={
              getStyleId(formData.shotGenerationStyle) === getStyleId(style) ||
              (!isDraftMode && !formData.shotGenerationStyle && style.isDefault)
            }
            onSelect={handleStyleSelect}
            onPreview={openVideoPreview}
            onDelete={handleDeleteStyle}
            isSuperAdmin={isSuperAdmin}
            deletingStyleId={deletingStyleId}
          />
        ))}
      </div>
    )

    return (
      <div className="shot-generation-style-container">
        {isLoadingStyles ? (
          <div className="loading-styles-container">
            <Icon type="loading" />
            <span>Loading styles...</span>
          </div>
        ) : (
          <div className="style-groups-wrapper">
            {/* System Styles Section */}
            {systemStyles?.length > 0 && (
              <div className="style-group-section">
                <div className="style-group-header">
                  <Icon type="appstore" />
                  <h3>System Styles</h3>
                  <span className="count-badge">{systemStyles.length}</span>
                </div>
                {renderStyleGrid(systemStyles)}
              </div>
            )}

            {/* Custom Styles Section */}
            <div className="style-group-section">
              <div className="style-group-header">
                <Icon type="user" />
                <h3>My Custom Styles</h3>
                <span className="count-badge">{customStyles?.length || 0}</span>
              </div>
              <div className="shot-generation-style-cards">
                {customStyles?.map(style => (
                  <StyleCard
                    key={getStyleId(style)}
                    style={style}
                    isSelected={
                      getStyleId(formData.shotGenerationStyle) ===
                      getStyleId(style)
                    }
                    onSelect={handleStyleSelect}
                    onPreview={openVideoPreview}
                    onDelete={handleDeleteStyle}
                    isSuperAdmin={isSuperAdmin}
                    deletingStyleId={deletingStyleId}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Video Preview Modal (For existing styles) */}
        <Modal
          visible={previewModalVisible}
          onCancel={() => setPreviewModalVisible(false)}
          footer={null}
          centered
          width={960}
          className="light-preview-modal"
          title={null}
          destroyOnClose
        >
          <div className="modal-content-wrapper">
            {/* Custom Header with Title and Close */}
            <div className="custom-modal-header">
              <div className="title-group">
                <Icon type="video-camera" className="header-icon" />
                <div className="text-info">
                  <h3>{previewStyleInfo.title}</h3>
                  <p>
                    {previewStyleInfo.isCustom
                      ? 'Custom Editing Style'
                      : 'System Editing Style'}
                  </p>
                </div>
              </div>
            </div>

            {/* Tab Navigation - Only show for custom styles */}
            {previewStyleInfo.isCustom &&
              previewStyleInfo.referenceVideos.length > 0 && (
                <div className="preview-nav-tabs">
                  <div
                    className={`nav-tab ${
                      activeVideoTab === 'thumbnail' ? 'active' : ''
                    }`}
                    onClick={() => setActiveVideoTab('thumbnail')}
                  >
                    <Icon type="play-square" />
                    <span>Sample Video</span>
                  </div>
                  <div
                    className={`nav-tab ${
                      activeVideoTab === 'reference' ? 'active' : ''
                    }`}
                    onClick={() => {
                      setActiveVideoTab('reference')
                      setCurrentPreviewIndex(0)
                    }}
                  >
                    <Icon type="database" />
                    <span>Reference Videos</span>
                    <span className="badge">
                      {previewStyleInfo.referenceVideos.length}
                    </span>
                  </div>
                </div>
              )}

            <div className="player-main-area">
              <div className="video-player-viewport">
                {getCurrentVideo() ? (
                  <video
                    key={getCurrentVideo()}
                    src={getCurrentVideo()}
                    controls
                    autoPlay
                    className="main-display-video"
                  />
                ) : (
                  <div className="video-error">
                    <Icon type="info-circle" />
                    <span>Video currently unavailable</span>
                  </div>
                )}
              </div>

              {/* Side Sidebar/Playlist (Only for custom styles with multiple videos) */}
              {activeVideoTab === 'reference' &&
                previewStyleInfo.referenceVideos.length > 0 && (
                  <div className="side-playlist">
                    <div className="playlist-title">
                      <span>Reference Gallery</span>
                    </div>
                    <div className="playlist-scroll">
                      {previewStyleInfo.referenceVideos.map((video, index) => {
                        const videoUrl =
                          typeof video === 'string' ? video : video?.video_link
                        return (
                          <div
                            key={index}
                            className={`side-item ${
                              index === currentPreviewIndex ? 'active' : ''
                            }`}
                            onClick={() => setCurrentPreviewIndex(index)}
                          >
                            <div className="side-thumb">
                              <video
                                src={videoUrl}
                                muted
                                onLoadedData={e => (e.target.currentTime = 1)}
                              />
                              {index === currentPreviewIndex && (
                                <div className="playing-state">
                                  <Icon type="caret-right" />
                                </div>
                              )}
                            </div>
                            <div className="side-info">Video {index + 1}</div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </Modal>

        {/* Create Style Modal Component */}
        <CreateStyleModal
          visible={isModalVisible}
          onCancel={closeCreateStyleModal}
          onCreate={createCustomStyle}
          isCreating={isCreatingStyle}
          uploadProgress={uploadProgress}
          creationProgress={creationProgress}
          currentAction={currentAction}
        />
      </div>
    )
  }
)

export default ShotGenerationStyle
